import React from 'react'
import { VALUE_LEVEL_COLUMN } from './components/TalentAreasTable/model'
import { fetchCandidates, fetchScoreRun } from 'api/candidates'
import { useModal, useLanguage } from 'hooks'
import { valueLevel } from 'components/shared/ScatterPlot/ScatterPlot'
import { capitalizeFirstLetter } from 'utils/stringFormat'
import { personalityOverviewTab } from 'components/modals/TestsReportModal/model'
import { TAB_NAMES } from './model'

const useModalQueries = () => {
  const language = useLanguage()

  const handleFetchCandidates = React.useCallback(
    async ({ id }) => await fetchCandidates({ id, query: { lang: language } }),
    [language],
  )

  return {
    handleFetchCandidates,
  }
}

const useFilterAndOrder = () => {
  const [ordering, setOrdering] = React.useState({
    ascending: null,
    descending: VALUE_LEVEL_COLUMN,
  })
  const [categoriesFilter, setCategoriesFilter] = React.useState()
  const [valueFilter, setValueFilter] = React.useState({
    highTalent: true,
    mediumTalent: true,
    lowTalent: true,
  })
  const setOrder = (direction, field) => {
    setOrdering({
      ascending: direction === 'ascending' ? field : null,
      descending: direction === 'descending' ? field : null,
    })
  }

  const setValue = (value, bool) => {
    setValueFilter({ ...valueFilter, [value]: bool })
  }

  const updateValueFilter = e => {
    const { name, checked } = e.target
    setValueFilter({ ...valueFilter, [name]: checked })
  }

  const updateCategoryFilter = e => {
    const { name, checked } = e.target
    setCategoriesFilter({ ...categoriesFilter, [name]: checked })
  }

  return {
    state: {
      ordering,
      categoriesFilter,
      valueFilter,
      updateValueFilter,
      updateCategoryFilter,
    },
    handlers: {
      setCategoriesFilter,
      setOrder,
      setValue,
    },
  }
}

export const useJobSeekerResultsModalState = state => {
  const { isOpen, jobSeeker } = state
  const { handleFetchCandidates } = useModalQueries()
  const language = useLanguage()
  const { isOpen: isGmaModalOpen, handleClose, handleOpen } = useModal()
  const [gmaScore, setGmaScore] = React.useState(null)
  const [showGma, setShowGma] = React.useState(false)
  const [showGmaToggle, setShowGmaToggle] = React.useState(false)
  const [talentData, setTalentData] = React.useState([])
  const [candidateResponse, setCandidateResponse] = React.useState(null)
  // Every algorithm version that has scored this candidate, newest first. More than one
  // means an older result was recalculated rather than replaced.
  const [scoreRuns, setScoreRuns] = React.useState([])
  // null = show the primary (current algorithm). Otherwise the id of an older run.
  const [selectedRunId, setSelectedRunId] = React.useState(null)
  // Older payloads are fetched on demand and kept, so switching tabs back is instant.
  const [runPayloads, setRunPayloads] = React.useState({})
  const [activeTab, setActiveTab] = React.useState(TAB_NAMES.OVERVIEW)
  const [personalityTab, setPersonalityTab] = React.useState(
    personalityOverviewTab().id,
  )
  const [selectedCandidates, setSelectedCandidates] = React.useState({})

  const {
    state: {
      ordering,
      categoriesFilter,
      valueFilter,
      updateValueFilter,
      updateCategoryFilter,
    },
    handlers: { setCategoriesFilter, setOrder, setValue },
  } = useFilterAndOrder()

  const handleTab = React.useCallback(tabId => {
    setActiveTab(tabId)
    setPersonalityTab(personalityOverviewTab().id)
  }, [])

  React.useEffect(() => {
    if (!jobSeeker?.id) {
      return
    }
    setSelectedCandidates({ [jobSeeker.id]: true })
  }, [jobSeeker?.id])

  React.useEffect(() => {
    if (!isOpen || !jobSeeker?.id) {
      return
    }
    setActiveTab(TAB_NAMES.OVERVIEW)
    setPersonalityTab(personalityOverviewTab().id)
  }, [isOpen, jobSeeker?.id])

  React.useEffect(() => {
    if (!isOpen) {
      setCandidateResponse(null)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen || !jobSeeker?.id) {
      return
    }
    let cancelled = false
    handleFetchCandidates({ id: jobSeeker.id }).then(result => {
      if (cancelled || !result?.response) {
        return
      }
      const response = result.response
      setCandidateResponse(response)
      setScoreRuns(response.score_runs || [])
      setSelectedRunId(null)
      setRunPayloads({})
      setShowGmaToggle(!!response.talent_scores?.interest_integration_with_gma)
    })
    return () => {
      cancelled = true
    }
  }, [isOpen, jobSeeker?.id, handleFetchCandidates])

  React.useEffect(() => {
    if (!selectedRunId || !jobSeeker?.id || runPayloads[selectedRunId]) {
      return
    }
    let cancelled = false
    fetchScoreRun({
      id: jobSeeker.id,
      runId: selectedRunId,
      query: { lang: language },
    }).then(result => {
      if (cancelled || !result?.response?.talent_scores) {
        return
      }
      setRunPayloads(current => ({
        ...current,
        [selectedRunId]: result.response.talent_scores,
      }))
    })
    return () => {
      cancelled = true
    }
  }, [selectedRunId, jobSeeker?.id, language, runPayloads])

  // Which result the whole modal renders: the primary, or the older run being viewed.
  const activeTalentScores = React.useMemo(() => {
    if (selectedRunId) {
      return runPayloads[selectedRunId] || null
    }
    return candidateResponse?.talent_scores || null
  }, [selectedRunId, runPayloads, candidateResponse])

  React.useEffect(() => {
    if (!activeTalentScores) {
      return
    }
    const dataKey = showGma
      ? 'interest_integration_with_gma'
      : 'interest_integration'
    const talents = activeTalentScores[dataKey]
    if (!talents) {
      // An older result may predate GMA blending entirely; fall back rather than blank out.
      const fallback = activeTalentScores.interest_integration
      if (!fallback) {
        return
      }
      setTalentData([])
      return
    }

    const translatedCategories = talents.reduce((acc, { code, category }) => {
      return { ...acc, [code]: category }
    }, {})

    setTalentData(
      talents.map(talent => ({
        area: talent.name,
        personalityScore: Math.round(talent.percentile * 100),
        interestScore: Math.round(talent.interest_rescaled),
        matchScore: Math.round(talent.match),
        rawCategory: talent.category,
        description: talent.description,
        category:
          language === 'en'
            ? talent.category
            : translatedCategories[talent.code],
        code: talent.code,
        value: talent.match,
      })),
    )

    const categories = [
      ...new Set(
        talents.map(talent =>
          language === 'en'
            ? talent.category
            : translatedCategories[talent.code],
        ),
      ),
    ].reduce((acc, category) => {
      acc[capitalizeFirstLetter(category)] = true
      return acc
    }, {})

    setCategoriesFilter(categories)
  }, [activeTalentScores, showGma, language])

  const filteredData = React.useMemo(() => {
    return talentData.filter(talent => {
      const level = valueLevel(talent.matchScore)
      const isHighValue = level === 'high'
      const isMediumValue = level === 'medium'
      const isLowValue = level === 'low'

      const valueFilterPass =
        (isHighValue && valueFilter.highTalent) ||
        (isMediumValue && valueFilter.mediumTalent) ||
        (isLowValue && valueFilter.lowTalent)

      const categoryFilterPass =
        categoriesFilter?.[capitalizeFirstLetter(talent.category)]

      return valueFilterPass && categoryFilterPass
    })
  }, [talentData, valueFilter, categoriesFilter])

  const orderedData = React.useMemo(() => {
    if (ordering.ascending) {
      return [...filteredData].sort((a, b) => {
        if (a[ordering.ascending] > b[ordering.ascending]) return 1
        if (a[ordering.ascending] < b[ordering.ascending]) return -1

        if (a[VALUE_LEVEL_COLUMN] > b[VALUE_LEVEL_COLUMN]) return 1
        if (a[VALUE_LEVEL_COLUMN] < b[VALUE_LEVEL_COLUMN]) return -1

        return 0
      })
    }
    if (ordering.descending) {
      return [...filteredData].sort((a, b) => {
        if (a[ordering.descending] < b[ordering.descending]) return 1
        if (a[ordering.descending] > b[ordering.descending]) return -1

        if (a[VALUE_LEVEL_COLUMN] < b[VALUE_LEVEL_COLUMN]) return 1
        if (a[VALUE_LEVEL_COLUMN] > b[VALUE_LEVEL_COLUMN]) return -1

        return 0
      })
    }
    return filteredData
  }, [filteredData, ordering])

  const handleConfirm = () => {
    handleClose()
  }

  return {
    state: {
      rawData: talentData,
      talentData: orderedData,
      ordering,
      categoriesFilter,
      valueFilter,
      isGmaOpen: isGmaModalOpen,
      gmaScore,
      showGma,
      showGmaToggle,
      language,
      candidateResponse,
      activeTab,
      personalityTab,
      selectedCandidates,
      scoreRuns,
      selectedRunId,
      // An older run whose payload has not arrived yet, so the panel can say so rather
      // than render an empty table.
      isLoadingRun: !!selectedRunId && !runPayloads[selectedRunId],
    },
    handlers: {
      setOrder,
      setValueFilter: setValue,
      updateValueFilter,
      updateCategoryFilter,
      handleOpen,
      handleClose,
      handleConfirm,
      setGmaScore,
      setShowGma,
      handleTab,
      setPersonalityTab,
      setSelectedCandidates,
      setSelectedRunId,
    },
  }
}
